// Display transforms never change the RES coordinate system or source image.
function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function displayRotation(info = {}) {
  // AicJpegS3 is generated with the RES page Rotation already applied. Only an
  // explicit display-only rotation (for example, a later manual UI rotation)
  // belongs on top of those pixels. DeskewAlpha remains authoritative for the
  // residual page skew and for mapping between image and RES coordinates.
  return finiteNumber(info.rotation) - finiteNumber(info.deskewAlpha);
}

export function rotationComponents(rotation = 0) {
  const radians = finiteNumber(rotation) * Math.PI / 180;
  const clean = value => Math.abs(value) < 1e-10 ? 0 : value;
  return { sin: clean(Math.sin(radians)), cos: clean(Math.cos(radians)), radians };
}

export function rotatedDimensions(width, height, rotation) {
  const { sin, cos } = rotationComponents(rotation);
  return {
    width: Math.ceil(width * Math.abs(cos) + height * Math.abs(sin)),
    height: Math.ceil(width * Math.abs(sin) + height * Math.abs(cos)),
  };
}

export function imageToCanvas(point, width, height, rotation) {
  const size = rotatedDimensions(width, height, rotation);
  const { sin, cos } = rotationComponents(rotation);
  const x = point.x - width / 2;
  const y = point.y - height / 2;
  return { x: x * cos - y * sin + size.width / 2, y: x * sin + y * cos + size.height / 2 };
}

export function canvasToImage(point, width, height, rotation) {
  const size = rotatedDimensions(width, height, rotation);
  const { sin, cos } = rotationComponents(rotation);
  const x = point.x - size.width / 2;
  const y = point.y - size.height / 2;
  return { x: x * cos + y * sin + width / 2, y: -x * sin + y * cos + height / 2 };
}

export function clientToCanvas(point, canvas) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: (point.x - rect.left) * canvas.width / rect.width,
    y: (point.y - rect.top) * canvas.height / rect.height,
  };
}

export function bounds(points) {
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

export function pixelBounds(points) {
  const rect = bounds(points);
  // Keep all four corners without expanding exact right angles by one pixel.
  const x = Math.floor(rect.x + 1e-8), y = Math.floor(rect.y + 1e-8);
  return { x, y, width: Math.ceil(rect.x + rect.width - 1e-8) - x,
    height: Math.ceil(rect.y + rect.height - 1e-8) - y };
}

function skewParameters(info = {}) {
  const slope = Math.tan(finiteNumber(info.deskewAlpha) * Math.PI / 180);
  return { slope, horizontal: Number(info.deskewMode) === 1,
    startY: slope > 0 ? 0 : finiteNumber(info.deskewHeight) };
}

export function resToImage(point, info) {
  const { slope, horizontal, startY } = skewParameters(info);
  const x = Number(point.x) - (horizontal ? slope * (Number(point.y) - startY) : 0);
  return { x, y: Number(point.y) + slope * x };
}

export function imageToRes(point, info) {
  const { slope, horizontal, startY } = skewParameters(info);
  // Undo the vertical shear before the horizontal shear: reverse operation order.
  const y = Number(point.y) - slope * Number(point.x);
  return { x: Number(point.x) + (horizontal ? slope * (y - startY) : 0), y };
}

export function entryImageRect(entry, info, scaleX = 1, scaleY = 1) {
  const values = [entry.Left, entry.Top, entry.Right, entry.Bottom].map(Number);
  if (!values.every(Number.isFinite) || values[2] <= values[0] || values[3] <= values[1]) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  return pixelBounds(rectCorners({ x: values[0], y: values[1],
    width: values[2] - values[0], height: values[3] - values[1] }).map(point => {
    const source = resToImage(point, info);
    return { x: source.x * scaleX, y: source.y * scaleY };
  }));
}

export function rectCorners(rect) {
  return [
    { x: rect.x, y: rect.y }, { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ];
}

export function selectionRect(start, end, magnifier) {
  const rect = bounds([start, end]);
  if (!magnifier || rect.x < magnifier.x || rect.y < magnifier.y
    || rect.x + rect.width > magnifier.x + magnifier.width
    || rect.y + rect.height > magnifier.y + magnifier.height) return rect;
  // Held magnifier displays a 2x crop centred at the same canvas location.
  return {
    x: magnifier.x + magnifier.width / 2 + (rect.x - magnifier.x - magnifier.width / 2) / 2,
    y: magnifier.y + magnifier.height / 2 + (rect.y - magnifier.y - magnifier.height / 2) / 2,
    width: rect.width / 2, height: rect.height / 2,
  };
}

export function fillCanvasBackground(context, color = 'white') {
  context.save();
  context.fillStyle = color;
  context.fillRect(0, 0, context.canvas.width, context.canvas.height);
  context.restore();
}

export function drawRotatedImage(context, image, rotation, highlights = {}) {
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  // Keep the visible page boundary rectangular even when the image pixels are
  // rotated to compensate for RES skew.
  fillCanvasBackground(context);
  context.save();
  context.translate(context.canvas.width / 2, context.canvas.height / 2);
  context.rotate(rotationComponents(rotation).radians);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  context.drawImage(image, -width / 2, -height / 2, width, height);
  Object.values(highlights).forEach(field => field.entries.forEach(highlight => {
    if (highlight.canvas.width > 0 && highlight.canvas.height > 0) {
      context.drawImage(highlight.canvas, highlight.x - width / 2, highlight.y - height / 2);
    }
  }));
  context.restore();
}
